import React, { useContext, useEffect, useState } from 'react';
import { appContext } from '../../App';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faMicrochip, 
  faBolt, 
  faChartLine, 
  faSpinner,
  faServer,
  faCoins,
  faArrowTrendUp,
  faArrowsRotate,
  faHashtag,
  faCheck
} from "@fortawesome/free-solid-svg-icons";
import axios from 'axios';

// Coin mapping based on algorithm
const algorithmToCoin = {
  'SHA256': 'Bitcoin (BTC)',
  'KHeavyHash': 'Kaspa (KAS)',
  'Blake3': 'Alephium (ALPH)',
  'ALPH': 'Alephium (ALPH)',
  'CuckooCycle': 'Cortex (CTXC)',
  'Eaglesong': 'Nervos (CKB)',
  'Etchash': 'Ethereum Classic (ETC)',
  'RandomX': 'Monero (XMR)',
};

// Coin image mapping with colors
const coinConfig = {
  'Bitcoin (BTC)': {
    image: '/assets/currency-btc.png',
    color: 'bg-orange-500/10',
    textColor: 'text-orange-600 dark:text-orange-400',
    borderColor: 'border-orange-500/20'
  },
  'Kaspa (KAS)': {
    image: '/assets/currency-kas.png',
    color: 'bg-blue-500/10',
    textColor: 'text-blue-600 dark:text-blue-400',
    borderColor: 'border-blue-500/20'
  },
  'Alephium (ALPH)': {
    image: '/assets/currency-alph.png',
    color: 'bg-purple-500/10',
    textColor: 'text-purple-600 dark:text-purple-400',
    borderColor: 'border-purple-500/20'
  },
  'Ethereum Classic (ETC)': {
    image: '/assets/currency-etc.png',
    color: 'bg-emerald-500/10',
    textColor: 'text-emerald-600 dark:text-emerald-400',
    borderColor: 'border-emerald-500/20'
  },
  'Monero (XMR)': {
    image: '/assets/currency-xmr.png',
    color: 'bg-red-500/10',
    textColor: 'text-red-600 dark:text-red-400',
    borderColor: 'border-red-500/20'
  },
  'default': {
    image: '/assets/currency-default.png',
    color: 'bg-slate-500/10',
    textColor: 'text-slate-600 dark:text-slate-400',
    borderColor: 'border-slate-500/20'
  }
};

// Interface for API response
interface MachineData {
  machineModelName: string;
  machineHashrate: number;
  hashrateUnit: string;
  machinePower: number;
  algorithm: string;
}

interface DisplayMiner {
  model: string;
  hashrate: string;
  power: string;
  coins: string;
  algorithm: string;
  efficiency: string;
  vendor: string;
}

// Helper function to extract vendor from model name
const extractVendor = (modelName: string): string => {
  const vendorPatterns = [
    { pattern: /bitmain|antminer/i, vendor: 'Bitmain' },
    { pattern: /microbt|whatsminer/i, vendor: 'MicroBT' },
    { pattern: /canaan|avalonn?/i, vendor: 'Canaan' },
    { pattern: /innosilicon/i, vendor: 'Innosilicon' },
    { pattern: /goldshell/i, vendor: 'Goldshell' },
    { pattern: /ipollo/i, vendor: 'iPollo' },
  ];

  for (const { pattern, vendor } of vendorPatterns) {
    if (pattern.test(modelName)) {
      return vendor;
    }
  }
  
  return 'Unknown Vendor';
};

// Vendor badge styling
const vendorStyles = {
  'Bitmain': {
    bg: 'bg-red-500/10',
    text: 'text-red-600 dark:text-red-400',
    border: 'border-red-500/20'
  },
  'MicroBT': {
    bg: 'bg-blue-500/10',
    text: 'text-blue-600 dark:text-blue-400',
    border: 'border-blue-500/20'
  },
  'Canaan': {
    bg: 'bg-emerald-500/10',
    text: 'text-emerald-600 dark:text-emerald-400',
    border: 'border-emerald-500/20'
  },
  'Innosilicon': {
    bg: 'bg-purple-500/10',
    text: 'text-purple-600 dark:text-purple-400',
    border: 'border-purple-500/20'
  },
  'Goldshell': {
    bg: 'bg-amber-500/10',
    text: 'text-amber-600 dark:text-amber-400',
    border: 'border-amber-500/20'
  },
  'iPollo': {
    bg: 'bg-indigo-500/10',
    text: 'text-indigo-600 dark:text-indigo-400',
    border: 'border-indigo-500/20'
  },
  'default': {
    bg: 'bg-slate-500/10',
    text: 'text-slate-600 dark:text-slate-400',
    border: 'border-slate-500/20'
  }
};

const MinerTable: React.FC = () => {
  const { darkMode } = useContext(appContext);
  const [minerData, setMinerData] = useState<DisplayMiner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'hashrate' | 'power' | 'efficiency'>('hashrate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [refreshing, setRefreshing] = useState(false);
  const [visibleCount, setVisibleCount] = useState(10);

  useEffect(() => {
    fetchMinerData();
  }, []);

  const fetchMinerData = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setRefreshing(true);
      setError(null);
      
      
      const response = await axios.get('/pool/machines');
      const data: MachineData[] = response.data;
      
      const transformedData: DisplayMiner[] = data.map(machine => {
        const coin = algorithmToCoin[machine.algorithm as keyof typeof algorithmToCoin] || 
                    `${machine.algorithm} (Unknown)`;
        const hashrate = `${machine.machineHashrate} ${machine.hashrateUnit}/s`;
        const power = `${machine.machinePower} W`;
        const vendor = extractVendor(machine.machineModelName);
        const efficiency = machine.machinePower > 0 
          ? `${(machine.machineHashrate / machine.machinePower).toFixed(2)} ${machine.hashrateUnit}/W`
          : 'N/A';

        return {
          model: machine.machineModelName,
          hashrate,
          power,
          coins: coin,
          algorithm: machine.algorithm,
          efficiency,
          vendor,
        };
      });

      // Apply sorting
      const sortedData = [...transformedData].sort((a, b) => {
        let valueA: number, valueB: number;
        
        switch (sortBy) {
          case 'hashrate':
            valueA = parseFloat(a.hashrate.split(' ')[0]);
            valueB = parseFloat(b.hashrate.split(' ')[0]);
            break;
          case 'power':
            valueA = parseFloat(a.power.split(' ')[0]);
            valueB = parseFloat(b.power.split(' ')[0]);
            break;
          case 'efficiency':
            valueA = parseFloat(a.efficiency.split(' ')[0]);
            valueB = parseFloat(b.efficiency.split(' ')[0]);
            break;
          default:
            return 0;
        }
        
        return sortOrder === 'desc' ? valueB - valueA : valueA - valueB;
      });

      setMinerData(sortedData);
    } catch (err) {
      console.error('Error fetching miner data:', err);
      
      if (axios.isAxiosError(err)) {
        setError(`API Error: ${err.response?.status || err.code} - ${err.message}`);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      }
      
      // Set empty data on error
      setMinerData([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getCoinConfig = (coins: string) => {
    return coinConfig[coins as keyof typeof coinConfig] || coinConfig.default;
  };

  const getVendorStyle = (vendor: string) => {
    return vendorStyles[vendor as keyof typeof vendorStyles] || vendorStyles.default;
  };

  const handleSort = (column: 'hashrate' | 'power' | 'efficiency') => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const SortIcon = ({ column }: { column: 'hashrate' | 'power' | 'efficiency' }) => {
    if (sortBy !== column) return null;
    return (
      <FontAwesomeIcon 
        icon={faArrowTrendUp} 
        className={`ml-2 text-xs ${sortOrder === 'desc' ? 'rotate-180' : ''}`}
      />
    );
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="flex flex-col items-center justify-center min-h-[500px]">
          
          <div className="relative mb-6">
            <FontAwesomeIcon 
              icon={faSpinner} 
              className="text-2xl text-white animate-spin"
            />
          </div>

          <div className="text-center pt-4">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-1">
              Loading Mining Equipment
            </h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              Fetching real-time data from our servers...
            </p>
          </div>

        </div>
      </div>

    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-12 py-24">
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
          <div className="w-20 h-20 rounded-2xl bg-red-500/10 flex items-center justify-center mb-4">
            <FontAwesomeIcon icon={faServer} className="text-2xl text-red-500" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Failed to Load Machine Details</h3>
          {/* <p className="text-slate-500 dark:text-slate-400 mb-4 max-w-md">{error}</p> */}
          <button
            onClick={() => fetchMinerData(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-12 py-24">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-8 gap-6">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-4">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 dark:text-white">
                Mining Hardware <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-600">Catalog</span>
              </h2>
            </div>
          </div>
          <p className="text-slate-600 dark:text-slate-300 max-w-2xl">
            Explore the latest mining equipment specifications, efficiency metrics, and supported algorithms.
          </p>
        </div>
      </div>

    {/* Desktop Table */}
    <div className="hidden lg:block" data-aos="fade-up">
      <div className="overflow-hidden bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-lg">
        <table className="w-full">
          <thead>
            <tr className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 border-b border-slate-200 dark:border-slate-800">
              <th className="px-6 py-4 text-left">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faMicrochip} className="text-slate-400" />
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Model</span>
                </div>
              </th>
              <th className="px-6 py-4 text-center">
                <button
                  onClick={() => handleSort('hashrate')}
                  className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider hover:text-blue-500 transition-colors"
                >
                  <FontAwesomeIcon icon={faHashtag} />
                  Hashrate
                  <SortIcon column="hashrate" />
                </button>
              </th>
              <th className="px-6 py-4 text-center">
                <button
                  onClick={() => handleSort('power')}
                  className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider hover:text-blue-500 transition-colors"
                >
                  <FontAwesomeIcon icon={faBolt} />
                  Power
                  <SortIcon column="power" />
                </button>
              </th>
              <th className="px-6 py-4 text-left">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faCoins} className="text-slate-400" />
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Coin / Algorithm</span>
                </div>
              </th>
              <th className="px-6 py-4 text-center">
                <button
                  onClick={() => handleSort('efficiency')}
                  className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider hover:text-blue-500 transition-colors"
                >
                  <FontAwesomeIcon icon={faChartLine} />
                  Efficiency
                  <SortIcon column="efficiency" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
            {minerData.slice(0, visibleCount).map((miner, index) => {
              const coinConfig = getCoinConfig(miner.coins);
              const vendorStyle = getVendorStyle(miner.vendor);
              return (
                <tr 
                  key={`${miner.model}-${index}`} 
                  className="group hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-all duration-200 hover:scale-[1.002]"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 flex items-center justify-center">
                        <FontAwesomeIcon icon={faMicrochip} className="text-lg text-slate-400 group-hover:text-blue-500 transition-colors" />
                      </div>
                      <div>
                        <h3 className="font-bold text-slate-900 dark:text-white group-hover:text-blue-500 transition-colors">
                          {miner.model}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <div className={`px-2 py-0.5 rounded-full ${vendorStyle.bg} ${vendorStyle.border} border`}>
                            <span className={`text-xs font-medium ${vendorStyle.text}`}>
                              {miner.vendor}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/10">
                      <FontAwesomeIcon icon={faHashtag} className="text-emerald-500 text-sm" />
                      <span className="font-bold text-emerald-700 dark:text-emerald-300">
                        {miner.hashrate}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500/10">
                      <FontAwesomeIcon icon={faBolt} className="text-amber-500" />
                      <span className="font-bold text-amber-700 dark:text-amber-300">
                        {miner.power}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl ${coinConfig.color} ${coinConfig.borderColor} border flex items-center justify-center`}>
                        <img 
                          src={coinConfig.image} 
                          alt={miner.coins} 
                          className="w-6 h-6"
                        />
                      </div>
                      <div>
                        <span className={`font-semibold block ${coinConfig.textColor}`}>
                          {miner.coins}
                        </span>
                        <span className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          {miner.algorithm}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500/10 to-purple-600/10">
                      <FontAwesomeIcon icon={faChartLine} className="text-blue-500 text-sm" />
                      <span className="font-bold text-blue-700 dark:text-blue-300">
                        {miner.efficiency}
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {/* Show More Button for Desktop */}
        {minerData.length > 10 && (
          <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
            <div className="flex justify-center">
              <button
                onClick={() => setVisibleCount(prev => prev + 10)}
                disabled={visibleCount >= minerData.length}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {visibleCount >= minerData.length ? (
                  'All Machines Loaded'
                ) : (
                  <>
                    Show More Machines
                    <span className="text-xs bg-white/20 px-2 py-1 rounded-full">
                      +{Math.min(10, minerData.length - visibleCount)} more
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Mobile Cards */}
    <div className="lg:hidden space-y-4" data-aos="fade-up">
      {minerData.slice(0, visibleCount).map((miner, index) => {
        const coinConfig = getCoinConfig(miner.coins);
        const vendorStyle = getVendorStyle(miner.vendor);
        return (
          <div 
            key={`${miner.model}-${index}`}
            className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-5 shadow-lg"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 flex items-center justify-center">
                  <FontAwesomeIcon icon={faMicrochip} className="text-slate-400" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 dark:text-white text-sm line-clamp-2">
                    {miner.model}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <div className={`px-2 py-0.5 rounded-full ${vendorStyle.bg} ${vendorStyle.border} border`}>
                      <span className={`text-xs font-medium ${vendorStyle.text}`}>
                        {miner.vendor}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Coin/Algorithm */}
            <div className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-slate-50 dark:bg-slate-800">
              <div className={`w-8 h-8 rounded-lg ${coinConfig.color} ${coinConfig.borderColor} border flex items-center justify-center`}>
                <img 
                  src={coinConfig.image} 
                  alt={miner.coins} 
                  className="w-5 h-5"
                />
              </div>
              <div>
                <div className={`text-sm font-semibold ${coinConfig.textColor}`}>
                  {miner.coins}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {miner.algorithm}
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <FontAwesomeIcon icon={faHashtag} className="text-emerald-500 text-xs" />
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Hashrate</span>
                </div>
                <div className="font-bold text-slate-900 dark:text-white text-sm">{miner.hashrate}</div>
              </div>
              <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <FontAwesomeIcon icon={faBolt} className="text-amber-500 text-xs" />
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Power</span>
                </div>
                <div className="font-bold text-slate-900 dark:text-white text-sm">{miner.power}</div>
              </div>
              <div className="bg-gradient-to-br from-blue-500/10 to-purple-600/10 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <FontAwesomeIcon icon={faChartLine} className="text-blue-500 text-xs" />
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Efficiency</span>
                </div>
                <div className="font-bold text-slate-900 dark:text-white text-sm">{miner.efficiency}</div>
              </div>
            </div>
          </div>
        );
      })}
      
      {/* Show More Button for Mobile */}
      {minerData.length > 10 && (
        <div className="pt-4">
          <button
            onClick={() => setVisibleCount(prev => prev + 10)}
            disabled={visibleCount >= minerData.length}
            className="w-full py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {visibleCount >= minerData.length ? (
              <>
                <FontAwesomeIcon icon={faCheck} className="text-sm" />
                All Machines Loaded
              </>
            ) : (
              <>
                Load More Machines
                <span className="text-xs bg-white/20 px-2 py-1 rounded-full">
                  +{Math.min(10, minerData.length - visibleCount)}
                </span>
              </>
            )}
          </button>
        </div>
      )}
    </div>

      {/* Empty State */}
      {minerData.length === 0 && !loading && !error && (
        <div className="text-center py-12">
          <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 flex items-center justify-center">
            <FontAwesomeIcon icon={faServer} className="text-2xl text-slate-400" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">No Mining Equipment Found</h3>
          <p className="text-slate-500 dark:text-slate-400 max-w-md mx-auto">
            No mining equipment data is currently available. Please check back later.
          </p>
        </div>
      )}
    </div>
  );
};

export default MinerTable;